import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AppContext } from './app-context';
import AssistantLauncher from '../components/assistant/AssistantLauncher';
import AssistantPanel from '../components/assistant/AssistantPanel';
import { capabilitiesFor } from '../components/assistant/assistantCapabilities';
import { assistantAvailableFor } from '../components/assistant/assistantAccess';
import type { AssistantLocation } from '../components/assistant/useAssistantConversation';
import { requestLocations } from '../components/location/requests/location-requests';
import { deriveLocationStatus } from '../types/LocationStatus';
import { useFeatureFlags } from '../hooks/routing/useFeatureFlags';
import { createPlanner } from '../components/assistant/planner';
import { createAgent } from '../components/assistant/agent';
import { createGeocode } from '../components/assistant/geocode';
import { GeocoderContext } from './geocoder-context';

/**
 * Mounts the assistant once for the whole signed-in app.
 *
 * Previously the launcher and panel lived inside Manage Locations, which tied a
 * general assistant to one page and meant its conversation was destroyed on
 * navigation. Mounting here — inside the router, above the routed content —
 * keeps a single instance alive across navigation, so a half-typed instruction
 * survives moving between pages.
 *
 * Gating is by account type, through three flags (see `assistantAccess`): a
 * master switch that alone enables admins, plus an opt-in each for suppliers and
 * buyers. The gating is about rollout confidence, not capability — every
 * endpoint is scoped to the signed-in user, and the assistant reports honestly
 * what it can do for each account type (see `assistantCapabilities`), so once an
 * audience is enabled it stays useful as new skills ship.
 */

interface AssistantContextValue {
  /** True when the assistant is mounted for this user at all. */
  available: boolean;
  open: boolean;
  openAssistant: () => void;
  closeAssistant: () => void;
  /**
   * The signed-in supplier's locations as the assistant currently sees them,
   * used to scope requests such as "all my active locations". Empty for
   * non-suppliers and while a fetch is in flight. Exposed so a consumer — and
   * the account-switch race test — can observe the list the widget is acting on.
   */
  locations: AssistantLocation[];
  /**
   * Registers a callback fired after the assistant successfully applies a
   * change, so a page showing location data can refresh itself. Returns an
   * unsubscribe function.
   */
  subscribeApplied: (listener: () => void) => () => void;
}

const noop = () => undefined;

const AssistantContext = createContext<AssistantContextValue>({
  available: false,
  open: false,
  openAssistant: noop,
  closeAssistant: noop,
  locations: [],
  subscribeApplied: () => noop,
});

export const useAssistant = (): AssistantContextValue => useContext(AssistantContext);

/**
 * Runs `onApplied` whenever the assistant saves a change.
 *
 * Lets a page keep its data fresh without the assistant having to know which
 * page is mounted. A page that does not care simply does not subscribe.
 */
export const useAssistantApplied = (onApplied: () => void): void => {
  const { subscribeApplied } = useAssistant();
  const latest = useRef(onApplied);
  latest.current = onApplied;

  useEffect(
    () => subscribeApplied(() => latest.current()),
    [subscribeApplied],
  );
};

interface AssistantProviderProps {
  children: React.ReactNode;
}

export const AssistantProvider = ({ children }: AssistantProviderProps) => {
  const { user } = useContext(AppContext);
  const accountId = user?.id;
  const {
    assistantChat,
    assistantChatSuppliers,
    assistantChatBuyers,
  } = useFeatureFlags();

  const [open, setOpen] = useState(false);
  const [locations, setLocations] = useState<AssistantLocation[]>([]);
  const listeners = useRef(new Set<() => void>());
  /*
   * Identifies the most recent location fetch so a slower earlier one cannot
   * win. This provider never unmounts across an admin's "log in as account"
   * switch, so a request fired for account A can still be in flight when the
   * account becomes B; without this guard its late response would call
   * `setLocations` and put A's names and statuses back on B's assistant — the
   * exact cross-account leak the clear-on-switch effect below exists to prevent.
   */
  const loadGeneration = useRef(0);

  const capabilities = useMemo(() => capabilitiesFor(user), [user]);
  const available = Boolean(
    user?.id
      && assistantAvailableFor(capabilities, {
        assistantChat,
        assistantChatSuppliers,
        assistantChatBuyers,
      }),
  );
  // Deterministic phrase-matching selector. A model-backed planner can be
  // slotted in later, but none ships today — the long tail is the agent's job.
  const planner = useMemo(() => createPlanner(), []);
  // The backend agent that answers questions no local skill covers. It has no
  // flag of its own: whenever the assistant is shown to this account it can use
  // the agent, and it still degrades to the local decline whenever a call fails.
  const agent = useMemo(
    () => createAgent({ enabled: available }),
    [available],
  );

  // The marketplace location search geocodes a typed place before searching.
  // The geocoder comes from the app-wide GeocoderContext (Google's browser
  // geocoder); wrapped here so a skill gets a plain function and so it degrades
  // to declining while the Google library is still loading.
  const { geocoder } = useContext(GeocoderContext);
  const geocode = useMemo(() => createGeocode(geocoder), [geocoder]);

  const loadLocations = useCallback(async () => {
    // Only suppliers own locations, and this endpoint is supplier-scoped.
    // `accountId` is read here rather than merely depended on so the callback's
    // identity changes when the signed-in account does, which is what drives
    // the refetch below.
    if (!accountId || capabilities.audience !== 'supplier') {
      setLocations([]);
      return;
    }
    // Claim a generation for this fetch; only its own response may apply, and
    // only if no later fetch (or account switch) has claimed a newer one.
    const generation = (loadGeneration.current += 1);
    try {
      const response = await requestLocations();
      if (generation !== loadGeneration.current) return;
      const rows: AssistantLocation[] = (response.body ?? []).map((raw: any) => ({
        id: raw.id,
        status: deriveLocationStatus(raw as { status: string; live: boolean }),
        locationName: raw.locationName,
      }));
      setLocations(rows);
    } catch {
      // The assistant degrades to name-based targeting without this list; it is
      // not worth a snackbar on a page the user may not have opened it from.
      if (generation === loadGeneration.current) setLocations([]);
    }
  }, [accountId, capabilities.audience]);

  const openAssistant = useCallback(() => setOpen(true), []);

  /*
   * Drop the previous account's locations the moment the account changes.
   *
   * This provider wraps every signed-in page and does **not** unmount when an
   * admin uses "log in as account", so without this the widget keeps whichever
   * list it loaded first: empty if it was opened as an admin (audience is not
   * supplier, so the fetch returns early), or the *previous* customer's names
   * if it was opened while impersonating someone else. An empty list makes
   * "turn off overages on all my active locations" answer "I cannot tell which
   * of your locations are active"; a stale one puts another account's names in
   * the example copy.
   */
  useEffect(() => {
    // Invalidate any in-flight fetch from the previous account as well as
    // clearing the list, so a response that is already on its way back cannot
    // repopulate before the next fetch even begins.
    loadGeneration.current += 1;
    setLocations([]);
  }, [accountId]);

  /*
   * Fetched on first open rather than on mount: most sessions never open the
   * assistant, so loading eagerly would add a request to every page load for
   * nothing. Re-runs when `loadLocations` changes identity, which is how an
   * account switch refills the list while the panel is already open.
   */
  useEffect(() => {
    if (!open) return;
    void loadLocations();
  }, [open, loadLocations]);

  const closeAssistant = useCallback(() => setOpen(false), []);

  const subscribeApplied = useCallback((listener: () => void) => {
    listeners.current.add(listener);
    return () => {
      listeners.current.delete(listener);
    };
  }, []);

  const handleApplied = useCallback(() => {
    void loadLocations();
    listeners.current.forEach((listener) => listener());
  }, [loadLocations]);

  const value = useMemo(
    () => ({ available, open, openAssistant, closeAssistant, locations, subscribeApplied }),
    [available, open, openAssistant, closeAssistant, locations, subscribeApplied],
  );

  return (
    <AssistantContext.Provider value={value}>
      {children}
      {available && (
        <>
          {/*
            No email-verification guard on the launcher. It used to be disabled
            for an unverified supplier because the assistant could only edit,
            but it now answers questions too — blocking the whole widget would
            withhold the part that still works. The write path refuses on its
            own, with a reason.
          */}
          <AssistantLauncher
            open={open}
            onClick={() => (open ? closeAssistant() : openAssistant())}
          />
          {open && (
            <AssistantPanel
              /*
               * Keyed by account so an account switch remounts the conversation.
               * `useAssistantConversation` seeds its messages from a lazy
               * `useState`, which runs once — without this the previous
               * account's transcript would stay on screen *and* the debounced
               * save would then write it under the new account's storage key,
               * carrying one customer's conversation into another's.
               */
              key={user!.id}
              open
              onClose={closeAssistant}
              userId={user!.id}
              locations={locations}
              capabilities={capabilities}
              planner={planner}
              agent={agent}
              geocode={geocode}
              onApplied={handleApplied}
            />
          )}
        </>
      )}
    </AssistantContext.Provider>
  );
};

export default AssistantProvider;
